import { useMemo, useState } from "react";

type DeployStatus =
  | "idle"
  | "triggering"
  | "queued"
  | "building"
  | "success"
  | "error"
  | "tracking_unavailable";

const TERMINAL_SUCCESS_STATES = new Set(["ready", "current", "published"]);
const TERMINAL_ERROR_STATES = new Set(["error", "failed"]);
const BUILDING_STATES = new Set(["building", "processing", "uploading"]);
const QUEUED_STATES = new Set(["new", "enqueued", "queued", "pending"]);
const ESTIMATED_BUILD_SECONDS = 180;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

function extractDeployId(payload: Record<string, unknown>): string | null {
  const id = payload.id;
  if (typeof id === "string" && id.length > 0) return id;

  const deployId = payload.deploy_id;
  if (typeof deployId === "string" && deployId.length > 0) return deployId;

  const deploy = payload.deploy;
  if (
    deploy &&
    typeof deploy === "object" &&
    "id" in deploy &&
    typeof deploy.id === "string"
  ) {
    return deploy.id;
  }

  return null;
}

function DeployButton() {
  const buildHookUrl = import.meta.env.VITE_NETLIFY_BUILD_HOOK_URL as
    | string
    | undefined;
  const [deployStatus, setDeployStatus] = useState<DeployStatus>("idle");
  const [statusText, setStatusText] = useState<string | null>(null);

  const isBusy = deployStatus === "triggering" || deployStatus === "queued" || deployStatus === "building";

  const buttonText = useMemo(() => {
    if (deployStatus === "triggering") return "Starting deploy...";
    if (deployStatus === "queued") return "Deploy queued...";
    if (deployStatus === "building") return "Deploy in progress...";
    if (deployStatus === "error") return "Retry deploy";
    return "Deploy Algarvian Roots";
  }, [deployStatus]);

  const statusColor = useMemo(() => {
    if (deployStatus === "error") return "#d32f2f";
    if (deployStatus === "success") return "#2e7d32";
    if (deployStatus === "tracking_unavailable") return "#ed6c02";
    return "#333";
  }, [deployStatus]);

  const runEstimatedBuild = async () => {
    setDeployStatus("building");
    for (let elapsed = 0; elapsed < ESTIMATED_BUILD_SECONDS; elapsed += 5) {
      const remaining = ESTIMATED_BUILD_SECONDS - elapsed;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      const secondsLabel = `${seconds}`.padStart(2, "0");
      setStatusText(
        `Deploy running (estimated). ${minutes}:${secondsLabel} remaining...`
      );
      await sleep(5000);
    }

    setDeployStatus("success");
    setStatusText("Estimated deploy window completed. Verify on Netlify.");
  };

  const pollDeployStatus = async (deployId: string) => {
    const deployStatusUrl = `https://api.netlify.com/api/v1/deploys/${deployId}`;

    for (let attempt = 0; attempt < 24; attempt += 1) {
      const response = await fetch(deployStatusUrl);
      if (!response.ok) {
        setDeployStatus("tracking_unavailable");
        setStatusText("Live tracking unavailable. Switching to estimated mode.");
        await runEstimatedBuild();
        return;
      }

      const payload = (await response.json()) as Record<string, unknown>;
      const stateValue =
        typeof payload.state === "string" ? payload.state.toLowerCase() : "";

      if (TERMINAL_SUCCESS_STATES.has(stateValue)) {
        setDeployStatus("success");
        setStatusText("Deploy completed successfully.");
        return;
      }

      if (TERMINAL_ERROR_STATES.has(stateValue)) {
        setDeployStatus("error");
        setStatusText("Deploy failed on Netlify.");
        return;
      }

      if (BUILDING_STATES.has(stateValue)) {
        setDeployStatus("building");
        setStatusText("Netlify is building the deploy...");
      } else if (QUEUED_STATES.has(stateValue)) {
        setDeployStatus("queued");
        setStatusText("Deploy is queued on Netlify...");
      } else {
        setDeployStatus("queued");
        setStatusText(`Deploy state: ${stateValue || "unknown"}`);
      }

      await sleep(5000);
    }

    setDeployStatus("tracking_unavailable");
    setStatusText("Polling timed out. Switching to estimated mode.");
    await runEstimatedBuild();
  };

  const triggerDeploy = async () => {
    if (isBusy) return;
    if (!buildHookUrl) {
      setDeployStatus("error");
      setStatusText("Missing VITE_NETLIFY_BUILD_HOOK_URL in environment.");
      return;
    }

    setDeployStatus("triggering");
    setStatusText("Triggering a new Netlify deploy...");

    try {
      const response = await fetch(buildHookUrl, {
        method: "POST",
      });
      const contentType = response.headers.get("content-type") ?? "";
      const payload = contentType.includes("application/json")
        ? ((await response.json()) as Record<string, unknown>)
        : {};

      if (!response.ok) {
        const errorMessage =
          typeof payload.error === "string"
            ? payload.error
            : "Failed to trigger deploy";
        throw new Error(errorMessage);
      }

      const deployId = extractDeployId(payload);
      if (!deployId) {
        setDeployStatus("tracking_unavailable");
        setStatusText("Deploy started. No ID returned, using estimated mode.");
        await runEstimatedBuild();
      } else {
        setDeployStatus("queued");
        setStatusText("Deploy queued on Netlify...");
        await pollDeployStatus(deployId);
      }
    } catch (error) {
      setDeployStatus("error");
      setStatusText(
        error instanceof Error ? error.message : "Unexpected deploy error"
      );
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        left: 20,
        bottom: 20,
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 8,
      }}
    >
      <button
        type="button"
        onClick={triggerDeploy}
        disabled={isBusy || !buildHookUrl}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #5f6bff",
          background: isBusy || !buildHookUrl ? "#9ea6ff" : "#5f6bff",
          color: "#fff",
          fontWeight: 600,
          cursor: isBusy || !buildHookUrl ? "not-allowed" : "pointer",
          boxShadow: "0 6px 20px rgba(95, 107, 255, 0.35)",
        }}
      >
        {buttonText}
      </button>
      {statusText && (
        <span
          style={{
            fontSize: 12,
            color: statusColor,
            background: "#fff",
            borderRadius: 6,
            padding: "4px 8px",
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
            maxWidth: 320,
            textAlign: "right",
          }}
        >
          {statusText}
        </span>
      )}
    </div>
  );
}

export default DeployButton;
